import { CircleUserRound, MoreHorizontal, Settings2 } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";

import { useCase } from "../../../contexts/CaseContext";
import { CASE_STATUS_TONE } from "../../matters/components/CaseStatusPill";
import {
  CaseDetailsPopover,
  caseSettingsPath,
  clientProfilePath
} from "../header/CaseDetailsPopover";
import { useSharedCaseDetails } from "../header/caseDetailsStore";
import { AssigneesMenu } from "../header/chips/AssigneesChip";
import { StatusMenu } from "../header/chips/StatusChip";

const MAX_TEAM = 3;

const createdLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return `Created ${date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" })
  })}`;
};

/**
 * Profile and Settings, the same actions as the details menu, at the menu
 * button's height (24px). The visible label stays short; hover uses the
 * header's title tooltip for the full name. Below 22rem (352px) the label
 * drops and the icon keeps that tooltip.
 */
const ExplorerAction: FC<{
  label: string;
  title: string;
  icon: typeof Settings2;
  onClick: () => void;
}> = ({ label, title, icon: Icon, onClick }) => (
  <Button
    variant="ghost"
    size="xs"
    aria-label={title}
    title={title}
    onClick={onClick}
  >
    <Icon />
    <span className="hidden @min-[22rem]/casehead:inline">{label}</span>
  </Button>
);

/**
 * The top of the case explorer: who the case is for, its workflow and age,
 * then its status and team. Profile and Settings sit beside the overflow,
 * which still opens the case details popover.
 */
export const CaseExplorerHeader: FC<{ individualId: string }> = ({
  individualId
}) => {
  const details = useSharedCaseDetails(individualId);
  const { case: caseInfo } = useCase();
  const navigate = useNavigate();

  if (!details) {
    return (
      <div className="explorer-card-head flex shrink-0 flex-col gap-2.5 px-3 pt-3 pb-2.5">
        <div className="flex items-center gap-2.5">
          <Skeleton className="size-7 rounded-md" />
          <div className="flex flex-1 flex-col gap-1">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    );
  }

  const meta = [details.workflow, createdLabel(caseInfo?.created_at)]
    .filter(Boolean)
    .join(" · ");
  const team = details.teamMembers.filter((m) =>
    details.assignedUserIds.includes(m.uid)
  );
  const extra = team.length - MAX_TEAM;

  const clientId = details.foreignNationalId;

  return (
    <div
      data-testid="case-explorer-header"
      className="@container/casehead explorer-card-head flex shrink-0 flex-col gap-2.5 px-3 pt-3 pb-2.5"
    >
      <div className="flex items-center gap-2.5">
        <Avatar size="md">
          {details.clientAvatarUrl ? (
            <AvatarImage src={details.clientAvatarUrl} alt="" />
          ) : null}
          <AvatarFallback>{details.clientInitials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="explorer-card-name truncate text-base font-medium text-fg">
            {details.clientName || "Unknown client"}
          </span>
          {meta ? (
            <span className="truncate text-sm text-fg-3">{meta}</span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {clientId ? (
            <ExplorerAction
              label="Profile"
              title="Client Profile"
              icon={CircleUserRound}
              onClick={() => navigate(clientProfilePath(clientId))}
            />
          ) : null}
          <ExplorerAction
            label="Settings"
            title="Case Settings"
            icon={Settings2}
            onClick={() => navigate(caseSettingsPath(details))}
          />
          <CaseDetailsPopover details={details}>
            <Button variant="ghost" size="icon-xs" aria-label="Case details">
              <MoreHorizontal />
            </Button>
          </CaseDetailsPopover>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <StatusMenu
          status={details.status}
          onSelect={(s) => void details.setStatus(s)}
          align="start"
        >
          <button
            type="button"
            aria-label={`Status: ${details.status}`}
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <StatusBadge tone={CASE_STATUS_TONE[details.status]} dot>
              {details.status}
            </StatusBadge>
          </button>
        </StatusMenu>
        <span className="flex-1" />
        <AssigneesMenu
          assigned={details.assignedUserIds}
          team={details.teamMembers}
          isLoading={details.isLoadingTeamMembers}
          isSaving={details.isSavingAssignees}
          onChange={details.setAssignees}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={details.isSavingAssignees}
              title={team.map((m) => m.label).join(", ") || undefined}
              className="flex h-6 items-center gap-1.5 rounded-md px-1 outline-none hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className="text-sm text-fg-3">Team</span>
              {team.length ? (
                <span className="flex -space-x-1">
                  {team.slice(0, MAX_TEAM).map((m) => (
                    <Avatar
                      key={m.uid}
                      size="xs"
                      className="ring-cutout ring-surface"
                    >
                      {m.avatarUrl ? (
                        <AvatarImage src={m.avatarUrl} alt="" />
                      ) : null}
                      <AvatarFallback>{m.initials}</AvatarFallback>
                    </Avatar>
                  ))}
                  {extra > 0 ? (
                    <Avatar size="xs" className="ring-cutout ring-surface">
                      <AvatarFallback>+{extra}</AvatarFallback>
                    </Avatar>
                  ) : null}
                </span>
              ) : (
                <span className="text-sm text-fg-4">None</span>
              )}
            </button>
          </DropdownMenuTrigger>
        </AssigneesMenu>
      </div>
    </div>
  );
};
